import type { BucketResult, LoanRecord } from '@apply/lending-limits';
import { ILoanHistorySummaryTableRow } from '../components/lendingLimitAccordionPanel/components/common/constants/tables/interfaces/ILoanHistorySummaryTableRow';

/**
 * Shared bits of the grid transforms.
 *
 * Rows are assembled as {@link RowAggregate}s — amounts alongside the raw
 * {@link LoanRecord}s — and only converted to grid rows at the end. Keeping the loan
 * records around until then means subtotal rows can concatenate the underlying loans
 * rather than merging already-numbered display rows, which would collide on `id`.
 */

/** The calc package rounds each aggregate it produces; sums across them need re-rounding. */
export const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const sum = (values: number[]): number => round2(values.reduce((total, value) => total + value, 0));

/**
 * One detail-table row before display mapping.
 *
 * "Outstanding" is the outstanding *balance*; "BFI Repays" is the interest-inclusive
 * repayment value — the same basis the lending limit's repayment credit uses. Counts are
 * not carried because they are always the length of the matching loan list.
 */
export interface RowAggregate {
    outstandingAmount: number;
    outstandingLoans: LoanRecord[];
    bfiRepaysAmount: number;
    bfiRepaysLoans: LoanRecord[];
}

export const EMPTY_ROW: RowAggregate = {
    outstandingAmount: 0,
    outstandingLoans: [],
    bfiRepaysAmount: 0,
    bfiRepaysLoans: [],
};

export const fromBucket = (bucket: BucketResult): RowAggregate => ({
    outstandingAmount: bucket.outstanding.outstandingBalance,
    outstandingLoans: bucket.outstanding.loans,
    bfiRepaysAmount: bucket.repaid.repaidValue,
    bfiRepaysLoans: bucket.repaid.loans,
});

/** Combine rows into a subtotal/total row. */
export const combineRows = (rows: RowAggregate[]): RowAggregate => ({
    outstandingAmount: sum(rows.map((row) => row.outstandingAmount)),
    outstandingLoans: rows.flatMap((row) => row.outstandingLoans),
    bfiRepaysAmount: sum(rows.map((row) => row.bfiRepaysAmount)),
    bfiRepaysLoans: rows.flatMap((row) => row.bfiRepaysLoans),
});

/**
 * Map loans to the loan-history grid's rows.
 *
 * `undefined` rather than `[]` for an empty list, so the eye icon has nothing to open —
 * matching how the grids read the field.
 *
 * `id` only has to be unique within the one list it is handed to (the modal's grid uses
 * it as `keyExpr`), so it is the position in that list. Neither the calculation nor the
 * BE response carries a loan identity we could use instead.
 */
export const toLoanHistoryRows = (
    loans: LoanRecord[]
): ILoanHistorySummaryTableRow[] | undefined =>
    loans.length === 0
        ? undefined
        : loans.map((loan, index) => ({
              id: index + 1,
              bfiNumber: loan.bfiNumber ?? '',
              loanNumber: loan.loanNumber,
              loanType: loan.loanType as ILoanHistorySummaryTableRow['loanType'],
              committedAmount: loan.commitmentAmount,
              amountRepaid: loan.repaidAmount ?? 0,
              repaymentDate: loan.repaymentDate ?? null,
              appraisedValue: loan.appraisedValue,
          }));

/** The six fields every detail-table row shares. */
export const detailRow = (row: RowAggregate) => ({
    outstandingAmount: row.outstandingAmount,
    outstandingNumber: row.outstandingLoans.length,
    outstandingLoans: toLoanHistoryRows(row.outstandingLoans),
    bfiRepaysAmount: row.bfiRepaysAmount,
    bfiRepaysNumber: row.bfiRepaysLoans.length,
    bfiRepaysLoans: toLoanHistoryRows(row.bfiRepaysLoans),
});
